import React from 'react';
import { FiBook, FiCheckCircle, FiCopy } from 'react-icons/fi';
import IconWrapper from '../../../IconWrapper';
import { config } from '../../../../config';
import {
  EXAMPLE_API_KEY,
  buildChatIngestExample,
  buildCurlExample,
  buildJavaExample,
  buildPythonExample,
  buildSocialThreadExample,
} from './apiKeyExamples';

interface DeveloperGuideTabProps {
  copySuccess: string | null;
  onCopy: (text: string, label: string) => void;
}

interface CodeBlockProps {
  title: string;
  label: string;
  code: string;
  copySuccess: string | null;
  onCopy: (text: string, label: string) => void;
}

const Card: React.FC<React.PropsWithChildren> = ({ children }) => (
  <section className="bg-white rounded-lg shadow-sm p-6">{children}</section>
);

const CodeBlock: React.FC<CodeBlockProps> = ({ title, label, code, copySuccess, onCopy }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      <button
        onClick={() => onCopy(code, label)}
        className="flex items-center text-sm text-blue-600 hover:text-blue-800"
      >
        {copySuccess === label ? (
          <>
            <IconWrapper icon={FiCheckCircle} className="w-4 h-4 mr-1 text-green-500" />
            Copied!
          </>
        ) : (
          <>
            <IconWrapper icon={FiCopy} className="w-4 h-4 mr-1" />
            Copy
          </>
        )}
      </button>
    </div>
    <pre className="bg-gray-50 rounded-md p-4 overflow-x-auto">
      <code className="text-sm text-gray-800">{code}</code>
    </pre>
  </div>
);

const RequestField: React.FC<{ name: string; description: string }> = ({ name, description }) => (
  <div>
    <dt className="font-medium text-gray-900 inline">{name}:</dt>
    <dd className="inline text-gray-600 ml-2">{description}</dd>
  </div>
);

const DeveloperGuideTab: React.FC<DeveloperGuideTabProps> = ({ copySuccess, onCopy }) => {
  const curlExample = buildCurlExample(config.API_URL);
  const pythonExample = buildPythonExample(config.API_URL);
  const javaExample = buildJavaExample(config.API_URL);
  const socialThreadExample = buildSocialThreadExample(config.API_URL);
  const chatIngestExample = buildChatIngestExample(config.API_URL);

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      <Card>
        <div className="flex items-center mb-4">
          <IconWrapper icon={FiBook} className="w-6 h-6 text-blue-600 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">API Integration Guide</h2>
        </div>
        <p className="text-gray-600 mb-4">
          The Good Outcomes API allows you to programmatically submit customer signals for
          analysis and processing. Use your API key to authenticate requests and submit
          signals from your applications.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            <strong>Quick Start:</strong> Copy one of the code examples below and replace
            the placeholder API key with your actual key from the API Keys tab.
          </p>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication</h3>
        <p className="text-gray-600 mb-4">
          All API requests must include your API key in the Authorization header:
        </p>
        <div className="bg-gray-50 rounded-md p-4">
          <div className="flex items-center justify-between">
            <code className="text-sm text-gray-800">Authorization: Bearer {EXAMPLE_API_KEY}</code>
            <button
              onClick={() => onCopy(`Authorization: Bearer ${EXAMPLE_API_KEY}`, 'auth-header')}
              className="ml-2 p-1 text-gray-500 hover:text-gray-700"
            >
              {copySuccess === 'auth-header' ? (
                <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
              ) : (
                <IconWrapper icon={FiCopy} className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Signal Submission Endpoint</h3>
        <p className="text-gray-600 mb-2">Submit customer signals for analysis:</p>
        <div className="bg-gray-50 rounded-md p-4">
          <code className="text-sm text-gray-800">POST {config.API_URL}/v1/signals/</code>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Request Fields</h3>
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Required Fields</h4>
            <div className="bg-gray-50 rounded-md p-4">
              <dl className="space-y-2 text-sm">
                <RequestField name="customer_name" description="Full name of the customer (string, max 255 chars)" />
                <RequestField name="verbatim" description="The complete customer message or complaint text (string, max 5000 chars)" />
                <RequestField name="channel" description='Communication channel, for example "email", "chat", "call_center", "social_media", "trustpilot", or any custom channel' />
              </dl>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Optional Fields</h4>
            <div className="bg-gray-50 rounded-md p-4">
              <dl className="space-y-2 text-sm">
                <RequestField name="customer_email" description="Customer's email address (string, max 255 chars)" />
                <RequestField name="customer_phone" description="Customer's phone number (string, max 50 chars)" />
                <RequestField name="customer_id" description="Your internal customer identifier (string, max 100 chars)" />
                <RequestField name="product_policy" description="Product or policy name/number (string, max 500 chars)" />
                <RequestField name="journey_stage" description='Customer journey stage, for example "purchase", "claim", "renewal", or "cancellation"' />
                <RequestField name="source" description="Specific source system or detail (string, max 100 chars)" />
                <RequestField name="metadata" description="Additional context as JSON object - ticket IDs, ratings, timestamps, etc." />
              </dl>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Response Format</h4>
            <pre className="bg-gray-50 rounded-md p-4 text-sm text-gray-800">
{`{
  "signal_id": "550e8400-e29b-41d4-a716-446655440000",
  "source_id": "SIG-550E8400",
  "status": "created",
  "message": "Signal created successfully"
}`}
            </pre>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <p className="text-sm text-amber-800">
              <strong>Note:</strong> The system automatically analyzes each signal to detect
              complaints, identify vulnerabilities, and calculate severity scores.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Thread Messages & Conversations (Ingest API)
        </h3>
        <p className="text-gray-600 mb-4">
          For conversations with multiple messages, use the Ingest API endpoint. It provides
          idempotent updates so you can add messages to existing conversations without duplicates.
        </p>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Key Feature:</strong> Use the same{' '}
            <code className="bg-blue-100 px-1 rounded">external_thread_id</code> to group
            multiple messages into one conversation.
          </p>
        </div>
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Endpoint</h4>
          <div className="bg-gray-50 rounded-md p-4">
            <code className="text-sm text-gray-800">POST {config.API_URL}/v1/signals/ingest</code>
          </div>
        </div>
        <div className="space-y-6">
          <CodeBlock
            title="Social Media Thread"
            label="social-thread"
            code={socialThreadExample}
            copySuccess={copySuccess}
            onCopy={onCopy}
          />
          <CodeBlock
            title="Live Chat Session"
            label="chat-ingest"
            code={chatIngestExample}
            copySuccess={copySuccess}
            onCopy={onCopy}
          />
          <div className="bg-gray-50 rounded-md p-4">
            <ul className="space-y-2 text-sm text-gray-700">
              <li><strong>Idempotent Updates:</strong> re-sending the same message ID will not create duplicates.</li>
              <li><strong>Thread Continuity:</strong> messages with the same thread ID stay grouped.</li>
              <li><strong>Full Context:</strong> the system analyzes the complete conversation for better insights.</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-medium text-gray-900 mb-6">Basic Signal Submission Examples</h3>
        <div className="space-y-6">
          <CodeBlock title="cURL" label="curl" code={curlExample} copySuccess={copySuccess} onCopy={onCopy} />
          <CodeBlock title="Python" label="python" code={pythonExample} copySuccess={copySuccess} onCopy={onCopy} />
          <CodeBlock title="Java" label="java" code={javaExample} copySuccess={copySuccess} onCopy={onCopy} />
        </div>
      </Card>
    </div>
  );
};

export default DeveloperGuideTab;
